import { useCallback } from 'react';
import { useReactFlow, XYPosition } from 'reactflow';
import { useWorkflowStore } from '@/state/workflow.state';
import { Node } from '@/types/node';

export function getCenterScreenPosition(
  flowContainerRect: DOMRect,
  screenToFlowPosition: (p: XYPosition) => XYPosition,
) {
  return screenToFlowPosition({
    x: (flowContainerRect.right - flowContainerRect.left - 240) / 2,
    y: (flowContainerRect.bottom - flowContainerRect.top - 120) / 2,
  });
}

export function useReactFlowViewportPositions() {
  const { screenToFlowPosition, flowToScreenPosition } = useReactFlow();
  const getReactFlowContainerRect = useWorkflowStore((s) => s.getReactFlowContainerRect);

  const getCenterPosition = useCallback(() => {
    const rect = getReactFlowContainerRect();
    return rect ? getCenterScreenPosition(rect, screenToFlowPosition) : { x: 0, y: 0 };
  }, [getReactFlowContainerRect, screenToFlowPosition]);

  const isNodeInViewport = useCallback(
    (node: Node): boolean => {
      const { width = 0, height = 0 } = node;
      const { x, y } = node.position;

      const containerRect = getReactFlowContainerRect();
      if (!containerRect) {
        return false;
      }

      const { x: nodeLeft, y: nodeTop } = flowToScreenPosition({ x, y });
      const { x: nodeRight, y: nodeBottom } = flowToScreenPosition({
        x: x + width,
        y: y + height,
      });

      // Container bounds in screen space
      const { left: containerLeft, right: containerRight, top: containerTop, bottom: containerBottom } = containerRect;

      // Check if entire node is contained within container bounds
      return (
        nodeLeft >= containerLeft &&
        nodeRight <= containerRight &&
        nodeTop >= containerTop &&
        nodeBottom <= containerBottom
      );
    },
    [getReactFlowContainerRect, flowToScreenPosition],
  );

  return { getCenterPosition, isNodeInViewport };
}
